import React, {useEffect, useState} from "react";
import axios from "../config/axios";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import LoadingScreen from "../components/loading-screen/LoadingScreen";
import {OrganizationRepository} from "../repositories/OrganizationRepository";
import {CategoryRepository} from "../repositories/CategoryRepository";
import {clearTokens, getAccessToken, REFRESH_TOKEN, saveTokens} from "../utils/authTokens";

export {TOKEN} from "../utils/authTokens";

const PASSWORD_SETUP_MESSAGE = "We moved to a new login system. Check your email for a link to set your new password.";

export const SignInContext = React.createContext({
  isAuth: null,
  currentUser: null,
  organizations: [],
  categories: [],
  onLogin: () => {
  },
  onRegister: () => {
  },
  onLogout: () => {
  },
  onOAuth: () => {
  },
  updateOrganizations: () => {
  },
  onForgotPassword: () => {},
  onChangePassword: () => {},
})

export const SignInContextProvider = props => {
  const [isAuth, setIsAuth] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    checkIsAuth();
    if (isAuth) {
      updateOrganizations();
    }
  }, [isAuth])

  const checkIsAuth = async () => {
    if (isAuth !== null) return;
    if (!getAccessToken()) {
      setIsAuth(false);
      return;
    }

    let user;
    try {
      // an expired access token is refreshed by the axios interceptor first
      const responseUser = await axios.post('/api/user/me');
      user = responseUser.data;
      user.roles = user.authorityList?.map(item => item?.authority) || [];
    } catch (error) {
      // Only a rejected session ends it; after a network or server error the tokens are kept, so reloading
      // the page continues the session
      if ([401, 404].includes(error?.response?.status)) {
        clearTokens();
      } else {
        console.error(error);
        toast.error("Failed to load your account, please reload the page");
      }
      setIsAuth(false);
      return;
    }
    setCurrentUser(user);
    setIsAuth(true);

    // failing to load these does not log the user out
    try {
      const isAdmin = user?.roles?.includes("admin");
      let responseOrganizations;
      let responseCategories;
      if (isAdmin) {
        responseOrganizations = await OrganizationRepository.getAll();
        responseCategories = await CategoryRepository.getAll();
      } else {
        responseOrganizations = await OrganizationRepository.getMyOrganizations();
        responseCategories = await CategoryRepository.getCategoriesByMember(user?.username);
      }
      setOrganizations(responseOrganizations?.data || []);
      setCategories(responseCategories?.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load your organizations");
    }
  }

  const onLogin = async ({username, password}) => {
    let tokens;
    try {
      // redirectUrl is where the "set your new password" email links to when the login returns PASSWORD_SETUP_REQUIRED
      const redirectUrl = `${window.location.origin}/user/reset-password`;
      const response = await axios.post('/api/auth/login', {username, password, redirectUrl});
      tokens = response.data;
    } catch (error) {
      // ErrorResponse from user management: {status, message, code}
      const body = error?.response?.data;
      const loginError = new Error(
        body?.code === "PASSWORD_SETUP_REQUIRED"
          ? PASSWORD_SETUP_MESSAGE
          : body?.message || "Failed to login"
      );
      loginError.code = body?.code;
      throw loginError;
    }
    saveTokens(tokens);

    try {
      if (tokens?.accessToken) {
        const responseUser = await axios.post('/api/user/me');
        const user = responseUser.data;
        user.roles = user.authorityList?.map(item => item?.authority) || [];
        
        setCurrentUser(user);
        setIsAuth(true);
        const searchParams = new URLSearchParams(window.location.search);
        const next = searchParams.get("next") || "/projects";
        if (searchParams.get("tour") === "true") {
          sessionStorage.setItem("tour", "true");
          sessionStorage.setItem("create_project_tour", "true");
          sessionStorage.setItem("project_journey", "true");
        }
        navigate(next);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to login");
    }
  }

  const onOAuth = async (token, refreshToken) => {
    if (token) {
      // The redirect carries no expiry, so assume the usual 15 minutes
      saveTokens({accessToken: token, refreshToken, expiresIn: 900});
      const responseUser = await axios.post('/api/user/me');
      const user = responseUser.data;
      user.roles = user.authorityList?.map(item => item?.authority) || [];

      setCurrentUser(user);
      setIsAuth(true);
      navigate("/projects")
    }
  }

  const onRegister = async ({username, email, password, name, lastname, phone_number, company_name}) => {
    try {
      const redirectUrl = window.location.origin;
      const response = await axios.post('/api/user', {
        username,
        email,
        password,
        name,
        lastname,
        phoneNumber: phone_number,
        companyName:company_name,
        redirectUrl
      });
      if (response.status === 200 || response.status === 201) {
        sessionStorage.setItem("registration_success", "true");
        sessionStorage.setItem("phone_number", phone_number);
        const next = new URLSearchParams(window.location.search).get("next");
        navigate(next ? `/login?next=${encodeURIComponent(next)}` : "/login");
      }
    } catch (error) {
      const message = "Failed to register";

      throw new Error(message);
    }
  }
  const onLogout = async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN);
      if (refreshToken) {
        // Ends the Keycloak session; the local logout goes ahead even if this fails
        await axios.post('/api/auth/logout', {refreshToken}).catch(() => {});
      }
      clearTokens();
      setIsAuth(false);
      setCurrentUser(null);
    } catch (error) {
      console.log(error);
      toast.error("Failed to logout");
    }
  }

  const updateOrganizations = () => {
    const isAdmin = currentUser?.roles?.includes("admin");

    if (isAdmin) {
      OrganizationRepository.getAll()
        .then(response => {
          setOrganizations(response?.data || []);
          console.log("User's organizations:", response)
        })
        .catch(error => {
          console.log(error);
          toast.error(error?.message || "Failed to fetch organizations");
        })
    } else {
      OrganizationRepository.getMyOrganizations()
        .then(response => {
          setOrganizations(response?.data || []);
          console.log("User's organizations:", response)
        })
        .catch(error => {
          console.log(error);
          toast.error(error?.message || "Failed to fetch organizations");
        })
    }
  }


  const onForgotPassword = async ({email}) => {
    await axios.post("/api/user/reset-password-token", {email}, {params: {email, redirectUrl: `${window.location.origin}/change-password`}})
  }

  const onChangePassword = async ({token, password}) => {
    return axios.post(`/api/user/reset-password`, null, {
      headers: {
        password: password
      },
      params: {token}
    })
  }

  return (
    <SignInContext.Provider
      value={{
        isAuth: isAuth,
        currentUser: currentUser,
        organizations: organizations,
        categories: categories,
        onLogin: onLogin,
        onLogout: onLogout,
        onRegister: onRegister,
        onOAuth: onOAuth,
        updateOrganizations: updateOrganizations,
        onChangePassword,
        onForgotPassword
      }}
    >
      {isAuth === null ? <LoadingScreen/> : props.children}
    </SignInContext.Provider>
  )
}
